import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { GoogleMapApiConfig } from "../../config/GoogleMapApiConfig";
import mapStyles from "../../config/mapStyles.json";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "16px",
};

const DRIVER_ICON_SVG = {
  path: "M12 2C8.13 2 5 5.13 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h1v2c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-2h1c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.87-3.13-7-7-7zm-2 14H9v-1h1v1zm4 0h-1v-1h1v1zm1.5-5.59l-.5.34V12c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1v-1.25l-.5-.34C8.06 9.45 7.5 8.27 7.5 9c0-2.48 2.02-4.5 4.5-4.5S16.5 6.52 16.5 9c0-.73-.56.45-1.5 1.41z",
  fillColor: "#009689",
  fillOpacity: 1,
  strokeWeight: 1,
  strokeColor: "#FFFFFF",
  scale: 1.8,
  anchor: { x: 12, y: 22 },
};

const CAR_ICON_URL = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
  <circle cx="24" cy="24" r="22" fill="#009689" stroke="#ffffff" stroke-width="3"/>
  <path d="M14 28l2-8c.5-1.5 1.5-2 3-2h10c1.5 0 2.5.5 3 2l2 8" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
  <rect x="13" y="28" width="22" height="8" rx="3" fill="#fff" opacity="0.9"/>
  <circle cx="17" cy="34" r="2" fill="#009689"/>
  <circle cx="31" cy="34" r="2" fill="#009689"/>
  <rect x="20" y="30" width="8" height="3" rx="1" fill="#009689" opacity="0.6"/>
</svg>
`);

const PICKUP_ICON_URL = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
  <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#4CAF50"/>
  <circle cx="12" cy="12" r="5" fill="#fff"/>
</svg>
`);

const DROP_ICON_URL = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
  <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#F44336"/>
  <circle cx="12" cy="12" r="5" fill="#fff"/>
</svg>
`);

const libraries = ["geometry"];

export default function DriverTrackingMap({ booking }) {
  const dispatch = useDispatch();
  const { fetchBookingLocations, stopLocationFetch } = api;
  const driverLocation = useSelector((state) => state.locationdata.coords);
  const [map, setMap] = useState(null);
  const [driverPosition, setDriverPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const prevPosition = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GoogleMapApiConfig,
    libraries,
  });

  const isActiveBooking = booking && ["ACCEPTED", "ARRIVED", "STARTED"].includes(booking.status);

  useEffect(() => {
    if (isActiveBooking && booking.id) {
      dispatch(fetchBookingLocations(booking.id));
    }
    return () => {
      if (booking && booking.id) {
        dispatch(stopLocationFetch(booking.id));
      }
    };
  }, [booking?.id, booking?.status, dispatch, fetchBookingLocations, stopLocationFetch, isActiveBooking]);

  useEffect(() => {
    if (driverLocation && driverLocation.lat && driverLocation.lng) {
      const newPos = { lat: driverLocation.lat, lng: driverLocation.lng };
      if (prevPosition.current) {
        const angle = computeHeading(prevPosition.current, newPos);
        if (angle !== 0) setHeading(angle);
      }
      prevPosition.current = newPos;
      setDriverPosition(newPos);
    }
  }, [driverLocation]);

  const computeHeading = (from, to) => {
    const lat1 = (from.lat * Math.PI) / 180;
    const lat2 = (to.lat * Math.PI) / 180;
    const dLng = ((to.lng - from.lng) * Math.PI) / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180) / Math.PI;
  };

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      if (booking) {
        const bounds = new window.google.maps.LatLngBounds();
        if (booking.pickup) bounds.extend({ lat: booking.pickup.lat, lng: booking.pickup.lng });
        if (booking.drop) bounds.extend({ lat: booking.drop.lat, lng: booking.drop.lng });
        if (driverPosition) bounds.extend(driverPosition);
        mapInstance.fitBounds(bounds, 60);
      }
    },
    [booking, driverPosition]
  );

  useEffect(() => {
    if (map && driverPosition && booking) {
      const bounds = new window.google.maps.LatLngBounds();
      if (booking.pickup) bounds.extend({ lat: booking.pickup.lat, lng: booking.pickup.lng });
      if (booking.drop) bounds.extend({ lat: booking.drop.lat, lng: booking.drop.lng });
      bounds.extend(driverPosition);
      map.fitBounds(bounds, 60);
    }
  }, [driverPosition, map, booking]);

  if (!isLoaded || !booking) return null;

  const center = booking.pickup
    ? { lat: booking.pickup.lat, lng: booking.pickup.lng }
    : { lat: 0, lng: 0 };

  return (
    <div style={{ position: "relative" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={14}
        onLoad={onLoad}
        options={{
          styles: mapStyles.light,
          disableDefaultUI: true,
          zoomControl: true,
          fullscreenControl: true,
        }}
      >
        {/* Driver marker */}
        {driverPosition && isActiveBooking && (
          <Marker
            position={driverPosition}
            icon={{
              url: CAR_ICON_URL,
              scaledSize: new window.google.maps.Size(44, 44),
              anchor: new window.google.maps.Point(22, 22),
            }}
            title="Driver"
            zIndex={100}
          />
        )}

        {/* Pickup marker */}
        {booking.pickup && (
          <Marker
            position={{ lat: booking.pickup.lat, lng: booking.pickup.lng }}
            icon={{
              url: PICKUP_ICON_URL,
              scaledSize: new window.google.maps.Size(32, 48),
              anchor: new window.google.maps.Point(16, 48),
            }}
            title={booking.pickupAddress || "Pickup"}
          />
        )}

        {/* Drop marker */}
        {booking.drop && (
          <Marker
            position={{ lat: booking.drop.lat, lng: booking.drop.lng }}
            icon={{
              url: DROP_ICON_URL,
              scaledSize: new window.google.maps.Size(32, 48),
              anchor: new window.google.maps.Point(16, 48),
            }}
            title={booking.dropAddress || "Drop-off"}
          />
        )}

        {/* Route polyline from booking coords */}
        {booking.coords && booking.coords.length > 0 && (
          <Polyline
            path={booking.coords}
            options={{
              strokeColor: "#009689",
              strokeOpacity: 0.8,
              strokeWeight: 4,
            }}
          />
        )}
      </GoogleMap>

      {/* Status badge */}
      {isActiveBooking && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            background: "#009689",
            color: "#fff",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(3,174,210,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#4CAF50",
              animation: "pulse 1.5s infinite",
            }}
          />
          LIVE TRACKING
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
